import { z } from 'zod';

import { CREATE_PLACEMENT_KEY_PATTERN } from '~/lib/ai/keyFormat';
import type { PlacementDraftInput } from '~/modules/query/ai/types';

export type { PlacementDraftInput } from '~/modules/query/ai/types';

export const PLACEMENT_KEY_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;

export type PlacementFieldErrors = Partial<Record<keyof PlacementDraftInput, string>>;

export interface PlacementDraftValidationError {
  fieldErrors: PlacementFieldErrors;
  message: string;
}

type PlacementDraftValidationResult =
  | { success: true; data: PlacementDraftInput }
  | { success: false; error: PlacementDraftValidationError };

/** The editor's schema; `useFormValidation` reads it field by field. */
export const placementDraftSchema = z.object({
  placementKey: z
    .string()
    .trim()
    .min(3, 'Placement key must be at least 3 characters')
    .max(64, 'Placement key must be 64 characters or less')
    .regex(PLACEMENT_KEY_PATTERN, 'Use lowercase letters, numbers, and separators . or _'),
  componentId: z.string().trim().min(1, 'Component ID is required'),
  componentVersion: z
    .string()
    .trim()
    .regex(
      /^\d+\.\d+\.\d+$/,
      'Component version must use semantic version format (major.minor.patch)',
    ),
  promptTemplate: z.string().trim().min(1, 'Prompt template is required'),
  model: z.string().trim().min(1, 'Model is required'),
  name: z.string().trim().max(200, 'Name must be 200 characters or less').optional(),
  description: z
    .string()
    .trim()
    .max(2000, 'Description must be 2000 characters or less')
    .optional(),
  changeNote: z.string().trim().optional().default(''),
  status: z.enum(['active', 'draft']),
});

/** The new-placement form: a name for the team and the key the app renders it with. */
export const placementCreateSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required.')
    .max(200, 'Name must be 200 characters or less')
    .regex(/[A-Za-z]/, 'Name must include at least one letter.'),
  placementKey: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Placement key is required.')
    .pipe(
      z
        .string()
        .min(3, 'Placement key must be at least 3 characters')
        .max(64, 'Placement key must be 64 characters or less')
        .regex(
          CREATE_PLACEMENT_KEY_PATTERN,
          'Use lowercase letters, numbers, and separators . or _',
        ),
    ),
});

export function normalizePlacementKey(placementKey: string): string {
  return placementKey.trim().toLowerCase();
}

export function validatePlacementDraftInput(
  input: PlacementDraftInput,
): PlacementDraftValidationResult {
  const normalisedInput: PlacementDraftInput = {
    ...input,
    placementKey: normalizePlacementKey(input.placementKey),
  };

  const parsed = placementDraftSchema.safeParse(normalisedInput);

  if (parsed.success) {
    return { success: true, data: parsed.data };
  }

  const fieldErrors: PlacementFieldErrors = {};

  for (const issue of parsed.error.issues) {
    const key = issue.path[0];
    if (typeof key !== 'string') {
      continue;
    }

    if (key in normalisedInput && fieldErrors[key as keyof PlacementDraftInput] === undefined) {
      fieldErrors[key as keyof PlacementDraftInput] = issue.message;
    }
  }

  return {
    success: false,
    error: {
      fieldErrors,
      message: 'Please fix the highlighted fields and try again.',
    },
  };
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseJsonRecord(
  rawValue: string,
  label: string,
): { success: true; data: Record<string, unknown> } | { success: false; error: string } {
  try {
    const parsed = JSON.parse(rawValue) as unknown;

    if (!isRecord(parsed)) {
      return { success: false, error: `${label} must be a JSON object` };
    }

    return { success: true, data: parsed };
  } catch {
    return { success: false, error: `${label} must be valid JSON` };
  }
}

const PLACEMENT_FIELDS = new Set<string>([
  'changeNote',
  'componentId',
  'componentVersion',
  'description',
  'model',
  'name',
  'placementKey',
  'promptTemplate',
  'status',
] satisfies Array<keyof PlacementDraftInput>);

/**
 * Reads a placement API error body's `fieldErrors` (service-ai's shape) into
 * the same keys the form uses. Unknown keys and non-string messages are
 * ignored so a driver-level payload never reaches a field.
 */
export function readPlacementFieldErrors(body: unknown): PlacementFieldErrors {
  if (!isRecord(body) || !isRecord(body.fieldErrors)) {
    return {};
  }

  const fieldErrors: PlacementFieldErrors = {};

  for (const [key, message] of Object.entries(body.fieldErrors)) {
    if (PLACEMENT_FIELDS.has(key) && typeof message === 'string' && message) {
      fieldErrors[key as keyof PlacementDraftInput] = message;
    }
  }

  return fieldErrors;
}
